import type { AppSupabaseClient } from '@/infrastructure/supabase/client'
import type { SupabaseClient } from '@supabase/supabase-js'
import { z } from 'zod'
import { reportUnexpectedError } from '@/shared/infrastructure/observability/reportUnexpectedError'
import { splitSearchTokens } from '@/shared/domain/searchTokens'
import { findClientIdsBySearch } from '@/infrastructure/supabase/partySearch'
import type { ContractRepository } from '../../application/ContractRepository'
import type {
  ContractDeletedListFilters,
  ContractDeletedPage,
  ContractEvent,
  ContractListFilters,
  ContractMarkerState,
  ContractRouteAssignment,
  ContractRecord,
  ContractSortKey,
  ContractStats,
  ContractStatus,
  ContractServiceLocation,
  ClientProperty,
} from '../../domain/contract'
import { dedupeFormattedAddressTail, hasContractPeriod } from '../../domain/contract'
import {
  applyContractAmendmentResultSchema,
  contractAmendmentRowSchema,
  createContractAmendmentResultSchema,
} from '../../domain/contractAmendment'
import { legacyContractRowSchema, mapLegacyContract } from './legacyContractMapper'
import type { ContractNote, ContractScheduleEntry, ContractSignature } from '../../domain/contract'
import { CONTRACT_ZONES_READ_INCOMPLETE, contractZoneTypes, type ContractZone } from '../../domain/zone'
import { contractBillingBlockers } from '../../domain/contractBilling'

const contractColumns =
  'id,numero,client_id,type,saison,prix,statut,status_v2,lead_id,renewed_from_contract_id,measurement_mode,standard_driveway_capacity,sent_at,last_sent_at,last_viewed_at,opened_since_last_send,submission_type,billing_name,billing_address_line1,billing_address_line2,billing_city,billing_province,billing_postal_code,billing_country,settings_version_id,date_signature,date_debut,date_fin,renouvellement,notes,latitude,longitude,superficie,adresse_geocodee,services,zone_desservie,exclusions,obligations_client,responsabilites,clause_annulation,clause_prix,clause_execution,clause_assurance,seuil_declenchement_cm,heure_premier_passage,nettoyage_final,mode_paiement,prix_taxes,sans_facturation,hidden_from_overview,created_at,updated_at,deleted_at,client:clients(numero,prenom,nom,entreprise,telephone,telephone_mobile,courriel,latitude,longitude)'

const serviceLocationRowSchema = z.object({
  id: z.uuid(),
  contract_id: z.uuid(),
  client_property_id: z.uuid().nullable(),
  address_line1: z.string(),
  address_line2: z.string().nullable(),
  city: z.string().nullable(),
  province: z.string().nullable(),
  postal_code: z.string().nullable(),
  country: z.string().nullable(),
  formatted_address: z.string(),
  latitude: z.coerce.number().nullable(),
  longitude: z.coerce.number().nullable(),
  services: z.array(z.string()),
  instructions: z.string().nullable(),
  measurement_mode: z.enum(['NONE', 'SATELLITE']),
  standard_driveway_capacity: z.coerce.number().int().nullable(),
  price_cents: z.coerce.number().int().nullable(),
  ordre: z.number().int().positive(),
  service_status: z.enum(['ACTIVE', 'SUSPENDED', 'REMOVED']),
})

const clientPropertyRowSchema = z.object({
  id: z.uuid(),
  client_id: z.uuid(),
  address_line1: z.string(),
  address_line2: z.string().nullable(),
  city: z.string().nullable(),
  province: z.string().nullable(),
  postal_code: z.string().nullable(),
  country: z.string(),
  latitude: z.coerce.number().nullable(),
  longitude: z.coerce.number().nullable(),
})

/** `ARCHIVED` dérive du soft delete : il n'est jamais filtrable par `statut`. */
const legacyStatus: Partial<Record<ContractStatus, string[]>> = {
  DRAFT: ['brouillon'],
  PENDING: ['a_signer', 'en_attente'],
  ACTIVE: ['actif'],
  SUSPENDED: ['suspendu'],
  EXPIRED: ['expire'],
  CANCELLED: ['annule'],
}

const createFromQuoteResultSchema = z.object({
  contractId: z.uuid(),
  number: z.string(),
  status: z.literal('DRAFT'),
  clientId: z.uuid(),
  season: z.string(),
  priceCents: z.coerce.number().int().nonnegative(),
  quoteId: z.uuid(),
  quoteNumber: z.string().nullable(),
})

interface SupabaseResponse {
  data: unknown
  error: { message: string; code?: string } | null
  count: number | null
}

function asResponse(value: unknown): SupabaseResponse {
  return value as SupabaseResponse
}

const polygonSchema = z.object({
  type: z.literal('Polygon'),
  coordinates: z.array(z.array(z.tuple([z.number(), z.number()]))),
})

const zoneRowSchema = z.object({
  id: z.uuid(),
  label: z.string(),
  type: z.enum(contractZoneTypes),
  measure_role: z.enum(['SURFACE', 'EXCLUSION']),
  geojson: polygonSchema,
  surface_m2: z.coerce.number().nonnegative(),
  gps_geojson: polygonSchema.nullable().optional(),
  gps_buffer_m: z.coerce.number().nullable().optional(),
  image_storage_path: z.string().nullable().optional(),
  ordre: z.number().int(),
  captured_at: z.string(),
})

const invoicesResultSchema = z.object({
  contractId: z.uuid(),
  invoices: z.number().int().nonnegative(),
  numbers: z.array(z.string()),
  totalWithTaxes: z.coerce.number(),
  gstPpm: z.coerce.number().int(),
  qstPpm: z.coerce.number().int(),
  unmappedTaxes: z.array(z.string()),
})

const pendingContractRowSchema = z.object({
  contract_id: z.uuid(),
  contract_number: z.string(),
  client_id: z.uuid(),
  client_name: z.string().nullable(),
  service_address: z.string().nullable(),
  client_phone: z.string().nullable(),
  client_email: z.string().nullable(),
  status_v2: z.enum(['PENDING_CLIENT', 'VIEWED']),
  total_amount: z.coerce.number().nullable(),
  sent_at: z.string().nullable(),
  last_follow_up_at: z.string().nullable(),
  next_follow_up_at: z.string().nullable(),
  follow_up_owner_id: z.uuid().nullable(),
  follow_up_owner_email: z.string().nullable(),
})

const scheduleFollowUpResultSchema = z.object({
  contractId: z.uuid(),
  nextFollowUpAt: z.string(),
  followUpOwnerId: z.uuid().nullable(),
})

const documentResultSchema = z.object({
  documentId: z.uuid(),
  version: z.number().int().positive(),
})

const markNotInterestedResultSchema = z.object({
  updated: z.array(z.object({ contractId: z.uuid(), invoicesCancelled: z.number().int() })),
  skipped: z.array(
    z.object({ contractId: z.uuid(), reason: z.enum(['NOT_FOUND', 'ALREADY_CLOSED', 'HAS_PAYMENTS']) }),
  ),
})

const sendDocumentResultSchema = z.object({
  contractId: z.uuid(),
  number: z.string().min(1),
  documentVersion: z.number().int().positive().optional(),
  replayed: z.boolean(),
})

const taxRateRowSchema = z.object({
  code: z.string().min(1),
  rate_ppm: z.coerce.number().int(),
})

const scheduleRowSchema = z.object({
  id: z.uuid(),
  description: z.string(),
  kind: z.enum(['PERCENTAGE', 'AMOUNT']),
  value: z.coerce.number(),
  amount_cents: z.coerce.number().int(),
  due_on: z.string().nullable(),
  ordre: z.number().int(),
})

const billingStateSchema = z.object({
  contractId: z.uuid(),
  priceCents: z.number().int(),
  priceTaxes: z.enum(['avant_taxes', 'apres_taxes', 'sans_taxes']),
  totalCents: z.number().int(),
  paidCents: z.number().int(),
  remainingCents: z.number().int(),
  blocker: z.enum(contractBillingBlockers).nullable(),
  entries: z.array(
    z.object({
      id: z.uuid(),
      order: z.number().int(),
      description: z.string(),
      amountCents: z.number().int(),
      dueOn: z.string().nullable(),
      invoiceId: z.uuid().nullable(),
      invoiceNumber: z.string().nullable(),
      paid: z.boolean(),
    }),
  ),
})

const billingRevisionResultSchema = z.object({
  remainingCents: z.number().int(),
  cancelledInvoices: z.number().int(),
  createdInvoices: z.array(z.string()),
})

const scheduleResultSchema = z.object({
  contractId: z.uuid(),
  entries: z.number().int().nonnegative(),
  scheduledCents: z.coerce.number().int(),
  totalCents: z.coerce.number().int(),
})

const signatureRowSchema = z.object({
  id: z.uuid(),
  signed_at: z.string(),
  signer_name: z.string(),
  signer_role: z.string().nullable(),
  method: z.enum(['EN_PERSONNE', 'COURRIEL', 'TELEPHONE', 'PAYMENT_PAGE', 'CLIENT_PORTAL', 'AUTRE']),
  proof: z.string().nullable(),
})

const contractEventRowSchema = z.object({
  id: z.uuid(),
  type: z.string(),
  created_at: z.string(),
  payload: z.unknown().nullable(),
  author: z.object({ nom: z.string().nullable(), email: z.string() }).nullable(),
})

const contractNoteColumns =
  'id,contract_id,message,created_at,updated_at,created_by,author:users!contract_notes_created_by_fkey(nom,email)'

const contractNoteRowSchema = z.object({
  id: z.uuid(),
  contract_id: z.uuid(),
  message: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
  created_by: z.string().nullable(),
  author: z.object({ nom: z.string().nullable(), email: z.string() }).nullable(),
})

function mapContractNoteRow(row: z.infer<typeof contractNoteRowSchema>): ContractNote {
  const authorName = row.author ? (row.author.nom?.trim() ?? '') || row.author.email : undefined
  return {
    id: row.id,
    contractId: row.contract_id,
    message: row.message,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    ...(row.created_by ? { authorId: row.created_by } : {}),
    ...(authorName ? { authorName } : {}),
  }
}

const signatureResultSchema = z.object({
  contractId: z.uuid(),
  number: z.string(),
  status: z.literal('ACTIVE'),
  signatureId: z.uuid(),
  signedAt: z.string(),
  measuredZones: z.coerce.number().int().nonnegative(),
})

const manualSignatureResultSchema = z.object({
  contractId: z.uuid(),
  number: z.string(),
  status: z.literal('SIGNED'),
  signatureId: z.uuid(),
  signedAt: z.string(),
  method: z.enum(['COURRIEL', 'TELEPHONE', 'EN_PERSONNE']),
})

const noInvoiceResultSchema = z.object({
  contractId: z.uuid(),
  number: z.string(),
  status: z.literal('ACTIVE'),
  priceCents: z.coerce.number().int().positive(),
})

const draftResultSchema = z.object({
  contractId: z.uuid(),
  number: z.string().min(1),
  status: z.literal('DRAFT'),
  clientId: z.uuid(),
  season: z.string(),
  measurementMode: z.enum(['NONE', 'SATELLITE']).optional(),
})

const replaceZonesResultSchema = z.object({
  contractId: z.uuid(),
  zones: z.number().int().nonnegative(),
  totalSurfaceM2: z.coerce.number().nonnegative(),
  gpsBufferMeters: z.coerce.number().nonnegative(),
  revision: z.number().int().nonnegative(),
})

const measurementStateSchema = z.object({
  revision: z.number().int().nonnegative(),
  sumSurfaceM2: z.coerce.number().nonnegative(),
  overlapM2: z.coerce.number().nonnegative(),
  excludedM2: z.coerce.number().nonnegative(),
  netSurfaceM2: z.coerce.number().nonnegative(),
})

function sanitizeSearch(value: string): string {
  return value
    .replace(/[%_,().'"\\]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80)
}

/**
 * Adresses de service : un Contrat en porte plusieurs (`contract_service_locations`),
 * absentes de `contracts`. Recherchées ici pour que la liste retrouve un Contrat
 * par n'importe laquelle de ses adresses.
 */
async function findContractIdsByServiceAddress(client: AppSupabaseClient, search: string): Promise<string[]> {
  const queryClient = client as unknown as SupabaseClient
  const response = asResponse(
    await queryClient
      .from('contract_service_locations')
      .select('contract_id')
      .neq('service_status', 'REMOVED')
      .or(
        [
          `formatted_address.ilike.%${search}%`,
          `address_line1.ilike.%${search}%`,
          `city.ilike.%${search}%`,
          `postal_code.ilike.%${search}%`,
        ].join(','),
      )
      .limit(500),
  )
  if (response.error || !Array.isArray(response.data)) return []
  const ids = new Set<string>()
  for (const row of response.data) {
    const parsed = z.object({ contract_id: z.string() }).safeParse(row)
    if (parsed.success) ids.add(parsed.data.contract_id)
  }
  return [...ids]
}

// Filtres PostgREST des trois statuts *affichés* — même dérivation que
// `deriveContractDisplayStatus` : `AWAITING_PAYMENT`/`SIGNED` l'emportent
// (18 sept 2026 : une signature manuelle par courriel, status_v2 = 'SIGNED',
// compte comme « en attente de paiement », pas « en attente de signature » —
// même bug que le badge d'en-tête), puis un Contrat actif jamais signé mais
// envoyé est « en attente de signature », sinon « Actif ».
const AWAITING_SIGNATURE_OR =
  'statut.in.(a_signer,en_attente),and(statut.eq.actif,date_signature.is.null,sent_at.not.is.null)'

// « Ouvert » = le client a ouvert la proposition depuis le dernier envoi/relance
// (`opened_since_last_send`, tenu par triggers). Même base que
// `AWAITING_SIGNATURE_OR` — pas un statut de plus, une séparation de celui-ci.
//
// Les quatre vues (signature, signature ouverte, paiement, actif) forment une
// partition : un Contrat en `AWAITING_PAYMENT`/`SIGNED` n'est jamais « en
// attente de signature », même si `statut` reste `a_signer` après une signature
// manuelle. Sans cette exclusion, un Contrat était compté deux fois et la somme
// des cartes dépassait « Tous les contrats ».
const NOT_AWAITING_PAYMENT = 'status_v2.is.null,status_v2.not.in.(AWAITING_PAYMENT,SIGNED)'

interface FilterBuilder<T> {
  or: (filters: string) => T
  eq: (c: string, v: boolean | string) => T
}

function awaitingSignatureQuery<T extends FilterBuilder<T>>(query: T, opened: boolean | 'ANY'): T {
  const awaiting = query.or(AWAITING_SIGNATURE_OR).or(NOT_AWAITING_PAYMENT)
  return opened === 'ANY' ? awaiting : awaiting.eq('opened_since_last_send', opened)
}

/** « Contrat actif » : signé, premier paiement reçu — ni signature ni paiement en attente. */
function activeQuery<T extends FilterBuilder<T>>(query: T): T {
  return query.eq('statut', 'actif').or(NOT_AWAITING_PAYMENT).or('date_signature.not.is.null,sent_at.is.null')
}

/** Brouillon, suspendu, expiré, annulé : leur `statut`, hors attente de paiement (qui l'emporte). */
function statutQuery<T extends FilterBuilder<T> & { in: (c: string, v: string[]) => T }>(
  query: T,
  status: ContractStatus,
): T {
  const byStatut = query.in('statut', legacyStatus[status] ?? []).or(NOT_AWAITING_PAYMENT)
  // « Pas intéressé » est un `annule` + `DECLINED` : il a sa propre vue, pas celle d'Annulé.
  return status === 'CANCELLED' ? byStatut.or('status_v2.is.null,status_v2.neq.DECLINED') : byStatut
}

/** « Pas intéressé » : refus du Client (`status_v2 = 'DECLINED'`). */
function notInterestedQuery<T extends FilterBuilder<T>>(query: T): T {
  return query.eq('status_v2', 'DECLINED')
}

/**
 * Statut affiché et VIP : filtres partagés avec les listes Commercial et Multilogements
 * (`SupabaseVnextContractRepository`), pour que les vues rapides comptent pareil partout.
 */
export function applyContractDisplayFilters<
  T extends FilterBuilder<T> & { in: (c: string, v: string[]) => T },
>(query: T, filters: Pick<ContractListFilters, 'displayStatus' | 'vip'>): T {
  let filtered = query
  if (filters.displayStatus === 'AWAITING_PAYMENT') {
    filtered = filtered.in('status_v2', ['AWAITING_PAYMENT', 'SIGNED'])
  } else if (filters.displayStatus === 'AWAITING_SIGNATURE_ANY') {
    filtered = awaitingSignatureQuery(filtered, 'ANY')
  } else if (filters.displayStatus === 'AWAITING_SIGNATURE') {
    filtered = awaitingSignatureQuery(filtered, false)
  } else if (filters.displayStatus === 'AWAITING_SIGNATURE_OPENED') {
    filtered = awaitingSignatureQuery(filtered, true)
  } else if (filters.displayStatus === 'ACTIVE') {
    filtered = activeQuery(filtered)
  } else if (filters.displayStatus === 'NOT_INTERESTED') {
    filtered = notInterestedQuery(filtered)
  } else if (filters.displayStatus) {
    filtered = statutQuery(filtered, filters.displayStatus)
  }
  if (filters.vip) filtered = activeQuery(filtered).eq('sans_facturation', true)
  return filtered
}

const sortColumns: Record<ContractSortKey, string[]> = {
  number: ['numero'],
  client: ['client(nom)', 'client(prenom)'],
  // Le statut affiché est dérivé : on groupe par les colonnes qui le portent.
  status: ['statut', 'status_v2', 'opened_since_last_send'],
  price: ['prix'],
}

const markerStateRowSchema = z.object({
  contract_id: z.string(),
  state: z.enum(['REQUIRED', 'IN_PLACE']).nullable(),
})

/**
 * Colonne Balises de la liste. RPC `security definer` : `contract_marker_tasks` n'est lisible
 * qu'avec `marker.read`. Une erreur (migration pas encore poussée, droit manquant) ne casse
 * jamais la liste : les contrats s'affichent simplement sans pastille.
 */
const routeAssignmentRowSchema = z.object({
  contract_id: z.string(),
  route: z
    .object({
      id: z.string(),
      nom: z.string().nullable(),
      couleur: z.string().nullable().optional(),
      deleted_at: z.string().nullable().optional(),
    })
    .nullable(),
})

/**
 * Routes actives des Contrats de la page (`route_contracts` non retirées, route non supprimée),
 * même lecture que la planification. Un Contrat à plusieurs adresses peut porter plusieurs routes;
 * chacune n'est listée qu'une fois. Lecture refusée ou en erreur : `null`, la colonne affiche « — ».
 */
async function loadRouteAssignments(
  client: AppSupabaseClient,
  contractIds: string[],
): Promise<Map<string, ContractRouteAssignment[]> | null> {
  if (contractIds.length === 0) return null
  const response = asResponse(
    await client
      .from('route_contracts')
      .select('contract_id,route:routes(id,nom,couleur,deleted_at)')
      .is('deleted_at', null)
      .in('contract_id', contractIds),
  )
  if (response.error || !Array.isArray(response.data)) return null
  const assignments = new Map<string, ContractRouteAssignment[]>()
  for (const candidate of response.data) {
    const parsed = routeAssignmentRowSchema.safeParse(candidate)
    if (!parsed.success) return null
    const { contract_id: contractId, route } = parsed.data
    if (!route || route.deleted_at) continue
    const list = assignments.get(contractId) ?? []
    if (!list.some((entry) => entry.routeId === route.id)) {
      list.push({
        routeId: route.id,
        name: route.nom ?? 'Route sans nom',
        ...(route.couleur ? { color: route.couleur } : {}),
      })
    }
    assignments.set(contractId, list)
  }
  return assignments
}

async function loadMarkerStates(
  client: AppSupabaseClient,
  contractIds: string[],
): Promise<Map<string, ContractMarkerState>> {
  const states = new Map<string, ContractMarkerState>()
  if (contractIds.length === 0) return states
  try {
    const rpcClient = client as unknown as SupabaseClient
    const response = asResponse(
      await rpcClient.rpc('contract_marker_state_v1', { p_contract_ids: contractIds }),
    )
    if (response.error || !Array.isArray(response.data)) return states
    for (const candidate of response.data) {
      const parsed = markerStateRowSchema.safeParse(candidate)
      if (parsed.success && parsed.data.state) states.set(parsed.data.contract_id, parsed.data.state)
    }
  } catch (error) {
    reportUnexpectedError(error, { feature: 'contracts', operation: 'load-marker-states' })
  }
  return states
}

const statsRowSchema = z.object({
  total: z.number(),
  draft: z.number(),
  pending: z.number(),
  active: z.number(),
  suspended: z.number(),
  expired: z.number(),
  cancelled: z.number(),
  awaiting_payment: z.number(),
  awaiting_signature: z.number(),
  awaiting_signature_opened: z.number(),
  active_signed: z.number(),
  vip: z.number(),
  not_interested: z.number(),
})

/** Les 13 compteurs en une requête (`contract_list_stats_v1`), mêmes règles que les filtres de la liste. */
async function loadStats(client: AppSupabaseClient): Promise<ContractStats> {
  const rpcClient = client as unknown as SupabaseClient
  const response = asResponse(await rpcClient.rpc('contract_list_stats_v1'))
  if (response.error) throw new Error(`CONTRACT_COUNT_FAILED:${response.error.code ?? 'UNKNOWN'}`)
  const row: unknown = Array.isArray(response.data) ? response.data[0] : response.data
  const parsed = statsRowSchema.safeParse(row)
  if (!parsed.success) throw new Error('CONTRACT_COUNT_RESPONSE_INVALID')
  const stats = parsed.data
  return {
    total: stats.total,
    draft: stats.draft,
    pending: stats.pending,
    active: stats.active,
    suspended: stats.suspended,
    expired: stats.expired,
    cancelled: stats.cancelled,
    awaitingPayment: stats.awaiting_payment,
    awaitingSignature: stats.awaiting_signature,
    awaitingSignatureOpened: stats.awaiting_signature_opened,
    activeSigned: stats.active_signed,
    vip: stats.vip,
    notInterested: stats.not_interested,
  }
}

function mapRows(data: unknown): { contracts: ContractRecord[]; anomalies: number } {
  if (!Array.isArray(data)) throw new Error('CONTRACT_LIST_RESPONSE_INVALID')
  const contracts: ContractRecord[] = []
  let anomalies = 0
  for (const candidate of data) {
    const parsed = legacyContractRowSchema.safeParse(candidate)
    if (!parsed.success) {
      anomalies += 1
      reportUnexpectedError(parsed.error, { feature: 'contracts', operation: 'map-list-row' })
      continue
    }
    try {
      const contract = mapLegacyContract(parsed.data)
      contracts.push(contract)
      // Un Contrat actif sans période ne permet pas de planifier la saison.
      if (contract.status === 'ACTIVE' && !hasContractPeriod(contract)) anomalies += 1
    } catch (error) {
      anomalies += 1
      reportUnexpectedError(error, { feature: 'contracts', operation: 'map-list-row' })
    }
  }
  return { contracts, anomalies }
}

export function createSupabaseContractRepository(
  client: AppSupabaseClient,
  // L'envoi passe par une Edge Function : il lui faut l'origine du projet et la
  // clé publique, que le client Supabase ne rend pas.
  supabaseUrl: string,
  anonKey: string,
): ContractRepository {
  return {
    async list(filters: ContractListFilters) {
      const from = (filters.page - 1) * filters.pageSize
      const to = from + filters.pageSize - 1
      let query = client.from('contracts').select(contractColumns, { count: 'exact' }).is('deleted_at', null)
      if (filters.sort) {
        for (const column of sortColumns[filters.sort.key]) {
          query = query.order(column, { ascending: filters.sort.direction === 'asc', nullsFirst: false })
        }
      }
      // Départage stable : sans lui, deux lignes ex æquo changent de page d'un appel à l'autre.
      query = query.order('updated_at', { ascending: false }).order('id').range(from, to)
      if (filters.status) {
        const legacy = legacyStatus[filters.status]
        // `ARCHIVED` n'existe pas dans la colonne : aucun résultat possible.
        if (!legacy) {
          return {
            items: [],
            page: filters.page,
            pageSize: filters.pageSize,
            total: 0,
            stats: await loadStats(client),
            meta: { source: 'supabase', retrievedAt: new Date().toISOString(), anomalies: 0 },
          }
        }
        query = query.in('statut', legacy)
      }
      query = applyContractDisplayFilters(query, filters)
      if (filters.clientId) query = query.eq('client_id', filters.clientId)
      // `contract_flow` (vNext) manque aux types générés : `filter` accepte la colonne telle quelle.
      if (filters.flow) query = query.filter('contract_flow', 'eq', filters.flow)
      const search = filters.search ? sanitizeSearch(filters.search) : ''
      if (search) {
        const byClient = await findClientIdsBySearch(client, splitSearchTokens(search))
        const byServiceAddress = await findContractIdsByServiceAddress(client, search)
        const clauses = [
          `numero.ilike.%${search}%`,
          `saison.ilike.%${search}%`,
          `notes.ilike.%${search}%`,
          `billing_name.ilike.%${search}%`,
          `billing_address_line1.ilike.%${search}%`,
          `billing_city.ilike.%${search}%`,
          `billing_postal_code.ilike.%${search}%`,
          ...(byClient.length > 0 ? [`client_id.in.(${byClient.join(',')})`] : []),
          ...(byServiceAddress.length > 0 ? [`id.in.(${byServiceAddress.join(',')})`] : []),
        ]
        query = query.or(clauses.join(','))
      }
      const [rawResponse, stats] = await Promise.all([query, loadStats(client)])
      const response = asResponse(rawResponse)
      if (response.error) throw new Error(`CONTRACT_LIST_FAILED:${response.error.code ?? 'UNKNOWN'}`)
      const mapped = mapRows(response.data)
      const pageIds = mapped.contracts.map((contract) => contract.id)
      const [markerStates, routes] = await Promise.all([
        loadMarkerStates(client, pageIds),
        loadRouteAssignments(client, pageIds),
      ])
      const items = mapped.contracts.map((contract) => {
        const markerState = markerStates.get(contract.id)
        const withMarker = markerState ? { ...contract, markerState } : contract
        return routes ? { ...withMarker, routeAssignments: routes.get(contract.id) ?? [] } : withMarker
      })
      return {
        items,
        page: filters.page,
        pageSize: filters.pageSize,
        total: response.count ?? mapped.contracts.length,
        stats,
        meta: { source: 'supabase', retrievedAt: new Date().toISOString(), anomalies: mapped.anomalies },
      }
    },
    async getByReference(reference) {
      const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        reference,
      )
      let query = client.from('contracts').select(contractColumns).is('deleted_at', null)
      query = uuid ? query.eq('id', reference) : query.eq('numero', reference)
      const response = asResponse(await query.maybeSingle())
      if (response.error) throw new Error(`CONTRACT_DETAIL_FAILED:${response.error.code ?? 'UNKNOWN'}`)
      if (response.data === null) return null
      const parsed = legacyContractRowSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('CONTRACT_DETAIL_RESPONSE_INVALID')
      const contract = mapLegacyContract(parsed.data)
      return {
        contract,
        meta: {
          source: 'supabase',
          retrievedAt: new Date().toISOString(),
          anomalies: contract.status === 'ACTIVE' && !hasContractPeriod(contract) ? 1 : 0,
        },
      }
    },
    async createFromQuote(command) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(
        await rpcClient.rpc('create_contract_from_quote_v1', {
          p_command: {
            idempotencyKey: command.idempotencyKey,
            quoteId: command.quoteId,
            season: command.season,
            ...(command.type ? { type: command.type } : {}),
          },
        }),
      )
      if (response.error) {
        throw new Error(
          response.error.message || `CONTRACT_CREATE_FAILED:${response.error.code ?? 'UNKNOWN'}`,
        )
      }
      const parsed = createFromQuoteResultSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('CONTRACT_CREATE_RESPONSE_INVALID')
      return parsed.data
    },

    async listZones(contractId) {
      const response = await client
        .from('contract_zones')
        .select(
          'id,label,type,measure_role,geojson,surface_m2,gps_geojson,gps_buffer_m,image_storage_path,ordre,captured_at',
        )
        .eq('contract_id', contractId)
        .is('deleted_at', null)
        // Les zones opérationnelles d'une adresse vNext (`zone_kind`) ne sont pas une mesure :
        // relues ici, elles seraient recopiées comme surfaces historiques au prochain enregistrement.
        .is('zone_kind', null)
        .order('ordre', { ascending: true })
      if (response.error) throw new Error(response.error.message)

      const zones: ContractZone[] = []
      for (const raw of response.data) {
        const parsed = zoneRowSchema.safeParse(raw)
        // Une zone illisible fait échouer toute la lecture : une mesure partielle,
        // réenregistrée, effacerait la zone écartée sans que personne le voie.
        if (!parsed.success) {
          reportUnexpectedError(parsed.error, { feature: 'contracts', operation: 'read-zones' })
          throw new Error(CONTRACT_ZONES_READ_INCOMPLETE)
        }
        const row = parsed.data
        zones.push({
          id: row.id,
          label: row.label,
          type: row.type,
          role: row.measure_role,
          geometry: row.geojson,
          surfaceM2: row.surface_m2,
          ...(row.gps_geojson ? { gpsGeometry: row.gps_geojson } : {}),
          ...(row.gps_buffer_m == null ? {} : { gpsBufferMeters: row.gps_buffer_m }),
          // La colonne vaut la chaîne vide quand la capture a échoué : la RPC
          // écrit `coalesce(imagePath, '')`. Une absence reste une absence.
          ...(row.image_storage_path ? { imagePath: row.image_storage_path } : {}),
          order: row.ordre,
          capturedAt: row.captured_at,
        })
      }
      return zones
    },

    async createCaptureUrl(storagePath) {
      // Le bucket est privé : le lien est demandé au clic et expire en cinq
      // minutes, jamais conservé.
      const storage = (client as unknown as SupabaseClient).storage
      const signed = await storage.from('contract-captures').createSignedUrl(storagePath, 300)
      if (signed.error) {
        reportUnexpectedError(signed.error, { feature: 'contracts', operation: 'sign-capture' })
        throw new Error('CONTRACT_CAPTURE_SIGNED_URL_FAILED')
      }
      return signed.data.signedUrl
    },

    async attachMeasurementPlan(contractId, revision, image) {
      const rpcClient = client as unknown as SupabaseClient
      const path = `${contractId}/plan-r${String(revision)}.png`
      // Bucket privé, sans remplacement ni suppression : un plan déjà téléversé par un essai précédent est
      // réutilisé tel quel (même révision, même mesure).
      const upload = await rpcClient.storage
        .from('contract-captures')
        .upload(path, image, { contentType: 'image/png', upsert: false })
      if (upload.error && !/exist|duplicate/i.test(upload.error.message)) {
        throw new Error('CONTRACT_PLAN_UPLOAD_FAILED')
      }
      const response = asResponse(
        await rpcClient.rpc('attach_contract_measurement_capture_v1', {
          p_command: { contractId, revision, imagePath: path },
        }),
      )
      if (response.error) throw new Error(response.error.message || 'CONTRACT_PLAN_ATTACH_FAILED')
      return path
    },

    async measurementState(contractId) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(
        await rpcClient.rpc('contract_measurement_state_v1', { p_contract_id: contractId }),
      )
      if (response.error) throw new Error(response.error.message || 'CONTRACT_MEASUREMENT_STATE_FAILED')
      const parsed = measurementStateSchema.safeParse(response.data)
      if (!parsed.success) throw new Error(CONTRACT_ZONES_READ_INCOMPLETE)
      return parsed.data
    },

    async replaceZones(command) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(
        await rpcClient.rpc('replace_contract_zones_v2', {
          p_command: {
            idempotencyKey: command.idempotencyKey,
            contractId: command.contractId,
            expectedRevision: command.expectedRevision,
            // La surface n'est jamais transmise : le serveur la recalcule.
            zones: command.zones.map((zone) => ({
              label: zone.label,
              type: zone.type,
              role: zone.role,
              geometry: zone.geometry,
              ...(zone.imagePath ? { imagePath: zone.imagePath } : {}),
            })),
            ...(command.gpsBufferMeters === undefined ? {} : { gpsBufferMeters: command.gpsBufferMeters }),
          },
        }),
      )
      if (response.error) {
        throw new Error(response.error.message || 'CONTRACT_ZONES_FAILED')
      }
      const parsed = replaceZonesResultSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('CONTRACT_ZONES_RESPONSE_INVALID')
      return parsed.data
    },
    async createDraft(command) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(
        await rpcClient.rpc('create_contract_draft_v2', {
          p_command: {
            idempotencyKey: command.idempotencyKey,
            clientId: command.clientId,
            season: command.season,
            ...(command.type ? { type: command.type } : {}),
            ...(command.serviceAddress ? { serviceAddress: command.serviceAddress } : {}),
            ...(command.latitude === undefined ? {} : { latitude: command.latitude }),
            ...(command.longitude === undefined ? {} : { longitude: command.longitude }),
            ...(command.leadId ? { leadId: command.leadId } : {}),
            ...(command.measurementMode ? { measurementMode: command.measurementMode } : {}),
            ...(command.standardDrivewayCapacity === undefined
              ? {}
              : { standardDrivewayCapacity: command.standardDrivewayCapacity }),
            ...(command.renewedFromContractId
              ? { renewedFromContractId: command.renewedFromContractId }
              : {}),
          },
        }),
      )
      if (response.error) throw new Error(response.error.message || 'CONTRACT_DRAFT_FAILED')
      const parsed = draftResultSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('CONTRACT_DRAFT_RESPONSE_INVALID')
      const { measurementMode, ...rest } = parsed.data
      return measurementMode === undefined ? rest : { ...rest, measurementMode }
    },

    async updateTerms(command) {
      const rpcClient = client as unknown as SupabaseClient
      // Les champs absents ne sont pas transmis : le serveur laisse alors
      // intacte la valeur déjà enregistrée par une autre étape.
      const payload = Object.fromEntries(Object.entries(command).filter(([, value]) => value !== undefined))
      const response = asResponse(await rpcClient.rpc('update_contract_terms_v2', { p_command: payload }))
      if (response.error) throw new Error(response.error.message || 'CONTRACT_TERMS_FAILED')
    },
    async listClientProperties(clientId) {
      const queryClient = client as unknown as SupabaseClient
      const response = asResponse(
        await queryClient
          .from('client_properties')
          .select(
            'id,client_id,address_line1,address_line2,city,province,postal_code,country,latitude,longitude',
          )
          .eq('client_id', clientId)
          .is('deleted_at', null)
          .order('created_at', { ascending: true }),
      )
      if (response.error) throw new Error(response.error.message)
      const parsedRows = z.array(clientPropertyRowSchema).safeParse(response.data)
      if (!parsedRows.success) throw new Error('CLIENT_PROPERTIES_RESPONSE_INVALID')
      return parsedRows.data.map<ClientProperty>((row) => {
        const address = {
          line1: row.address_line1,
          ...(row.address_line2 ? { line2: row.address_line2 } : {}),
          ...(row.city ? { city: row.city } : {}),
          ...(row.province ? { province: row.province } : {}),
          ...(row.postal_code ? { postalCode: row.postal_code } : {}),
          ...(row.country ? { country: row.country } : {}),
        }
        return {
          id: row.id,
          clientId: row.client_id,
          address,
          formattedAddress: dedupeFormattedAddressTail(
            [address.line1, address.city, address.province, address.postalCode].filter(Boolean).join(', '),
          ),
          ...(row.latitude == null ? {} : { latitude: row.latitude }),
          ...(row.longitude == null ? {} : { longitude: row.longitude }),
        }
      })
    },
    async listServiceLocations(contractId, options) {
      // La migration V2 précède volontairement la prochaine régénération du
      // schéma Supabase local. Cette requête reste validée par Zod à la frontière.
      const queryClient = client as unknown as SupabaseClient
      const query = queryClient
        .from('contract_service_locations')
        .select(
          'id,contract_id,client_property_id,address_line1,address_line2,city,province,postal_code,country,formatted_address,latitude,longitude,services,instructions,measurement_mode,standard_driveway_capacity,price_cents,ordre,service_status',
        )
        .eq('contract_id', contractId)
      // Une adresse retirée n'est lue que par la section « Adresses retirées » de la fiche.
      const response = asResponse(
        await (
          options?.removed ? query.eq('service_status', 'REMOVED') : query.neq('service_status', 'REMOVED')
        ).order('ordre', { ascending: true }),
      )
      if (response.error) throw new Error(response.error.message)
      const parsedRows = z.array(serviceLocationRowSchema).safeParse(response.data)
      if (!parsedRows.success) throw new Error('CONTRACT_SERVICE_LOCATIONS_RESPONSE_INVALID')
      const locations: ContractServiceLocation[] = []
      for (const row of parsedRows.data) {
        locations.push({
          id: row.id,
          contractId: row.contract_id,
          ...(row.client_property_id ? { clientPropertyId: row.client_property_id } : {}),
          address: {
            line1: row.address_line1,
            ...(row.address_line2 ? { line2: row.address_line2 } : {}),
            ...(row.city ? { city: row.city } : {}),
            ...(row.province ? { province: row.province } : {}),
            ...(row.postal_code ? { postalCode: row.postal_code } : {}),
            ...(row.country ? { country: row.country } : {}),
          },
          formattedAddress: dedupeFormattedAddressTail(row.formatted_address),
          ...(row.latitude == null ? {} : { latitude: row.latitude }),
          ...(row.longitude == null ? {} : { longitude: row.longitude }),
          services: row.services,
          ...(row.instructions ? { instructions: row.instructions } : {}),
          measurementMode: row.measurement_mode,
          ...(row.standard_driveway_capacity == null
            ? {}
            : { standardDrivewayCapacity: row.standard_driveway_capacity }),
          ...(row.price_cents == null ? {} : { priceCents: row.price_cents }),
          order: row.ordre,
          ...(row.service_status === 'ACTIVE' ? {} : { serviceStatus: row.service_status }),
        })
      }
      return locations
    },
    async configureLocations(command) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(
        await rpcClient.rpc('configure_contract_locations_v1', {
          p_command: {
            idempotencyKey: command.idempotencyKey,
            contractId: command.contractId,
            submissionType: command.submissionType,
            billingAddress: command.billingAddress,
            locations: command.locations.map((location) => ({
              ...location,
              formattedAddress: dedupeFormattedAddressTail(
                location.formattedAddress ??
                  [
                    location.address.line1,
                    location.address.city,
                    location.address.province,
                    location.address.postalCode,
                  ]
                    .filter(Boolean)
                    .join(', '),
              ),
            })),
          },
        }),
      )
      if (response.error) throw new Error(response.error.message || 'CONTRACT_LOCATIONS_FAILED')
    },
    async listActiveTaxRatesPpm() {
      // Même source que la facturation : la fonction serveur rend les taux du
      // profil fiscal actif. Aucun taux n'est recalculé côté navigateur.
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(await rpcClient.rpc('active_tax_rates_ppm_v1'))
      if (response.error) throw new Error(response.error.message || 'CONTRACT_TAX_RATES_FAILED')
      const parsed = z.array(taxRateRowSchema).safeParse(response.data)
      if (!parsed.success) throw new Error('CONTRACT_TAX_RATES_RESPONSE_INVALID')
      return parsed.data.map((row) => ({ code: row.code, ratePpm: row.rate_ppm }))
    },
    async listSchedule(contractId) {
      const response = await client
        .from('contract_schedule_entries')
        .select('id,description,kind,value,amount_cents,due_on,ordre')
        .eq('contract_id', contractId)
        .order('ordre', { ascending: true })
      if (response.error) throw new Error(response.error.message)
      const entries: ContractScheduleEntry[] = []
      for (const raw of response.data) {
        const parsed = scheduleRowSchema.safeParse(raw)
        if (!parsed.success) continue
        const row = parsed.data
        entries.push({
          id: row.id,
          description: row.description,
          kind: row.kind,
          value: row.value,
          amountCents: row.amount_cents,
          ...(row.due_on ? { dueOn: row.due_on } : {}),
          order: row.ordre,
        })
      }
      return entries
    },

    async replaceSchedule(command) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(
        await rpcClient.rpc('replace_contract_schedule_v1', {
          p_command: {
            idempotencyKey: command.idempotencyKey,
            contractId: command.contractId,
            // Les montants ne partent jamais : le serveur les résout.
            entries: command.entries.map((entry) => ({
              description: entry.description,
              kind: entry.kind,
              value: entry.value,
              ...(entry.dueOn ? { dueOn: entry.dueOn } : {}),
            })),
          },
        }),
      )
      if (response.error) throw new Error(response.error.message || 'CONTRACT_SCHEDULE_FAILED')
      const parsed = scheduleResultSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('CONTRACT_SCHEDULE_RESPONSE_INVALID')
      return parsed.data
    },

    async listEvents(contractId) {
      const response = await client
        .from('contract_events')
        .select('id,type,created_at,payload,author:users!contract_events_created_by_fkey(nom,email)')
        .eq('contract_id', contractId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
      if (response.error) throw new Error(response.error.message)
      const events: ContractEvent[] = []
      for (const raw of response.data) {
        const parsed = contractEventRowSchema.safeParse(raw)
        if (!parsed.success) continue
        const row = parsed.data
        const payload =
          row.payload && typeof row.payload === 'object' && !Array.isArray(row.payload)
            ? (row.payload as Record<string, unknown>)
            : undefined
        const authorName = row.author ? (row.author.nom?.trim() ?? '') || row.author.email : undefined
        events.push({
          id: row.id,
          type: row.type,
          createdAt: row.created_at,
          ...(authorName ? { authorName } : {}),
          ...(payload ? { payload } : {}),
        })
      }
      return events
    },

    async listNotes(contractId) {
      const response = await client
        .from('contract_notes')
        .select(contractNoteColumns)
        .eq('contract_id', contractId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
      if (response.error) throw new Error(`CONTRACT_NOTES_LIST_FAILED:${response.error.code || 'UNKNOWN'}`)
      const notes: ContractNote[] = []
      for (const raw of response.data) {
        const parsed = contractNoteRowSchema.safeParse(raw)
        if (parsed.success) notes.push(mapContractNoteRow(parsed.data))
      }
      return notes
    },

    async createNote(command) {
      const response = await client
        .from('contract_notes')
        .insert({ contract_id: command.contractId, message: command.message })
        .select(contractNoteColumns)
        .single()
      if (response.error) throw new Error(`CONTRACT_NOTE_CREATE_FAILED:${response.error.code || 'UNKNOWN'}`)
      const parsed = contractNoteRowSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('CONTRACT_NOTE_CREATE_RESPONSE_INVALID')
      return mapContractNoteRow(parsed.data)
    },

    async updateNote(command) {
      const response = await client
        .from('contract_notes')
        .update({ message: command.message })
        .eq('id', command.id)
        .select(contractNoteColumns)
        .single()
      if (response.error) throw new Error(`CONTRACT_NOTE_UPDATE_FAILED:${response.error.code || 'UNKNOWN'}`)
      const parsed = contractNoteRowSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('CONTRACT_NOTE_UPDATE_RESPONSE_INVALID')
      return mapContractNoteRow(parsed.data)
    },

    async softDeleteNote(noteId) {
      // Suppression douce seulement : la table n'a aucune policy DELETE.
      const response = await client
        .from('contract_notes')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', noteId)
        .select('id')
        .single()
      if (response.error) throw new Error(`CONTRACT_NOTE_DELETE_FAILED:${response.error.code || 'UNKNOWN'}`)
    },

    async listSignatures(contractId) {
      const response = await client
        .from('contract_signatures')
        .select('id,signed_at,signer_name,signer_role,method,proof')
        .eq('contract_id', contractId)
        .order('signed_at', { ascending: false })
      if (response.error) throw new Error(response.error.message)
      const signatures: ContractSignature[] = []
      for (const raw of response.data) {
        const parsed = signatureRowSchema.safeParse(raw)
        if (!parsed.success) continue
        const row = parsed.data
        signatures.push({
          id: row.id,
          signedAt: row.signed_at,
          signerName: row.signer_name,
          ...(row.signer_role ? { signerRole: row.signer_role } : {}),
          method: row.method,
          ...(row.proof ? { proof: row.proof } : {}),
        })
      }
      return signatures
    },

    async recordSignature(command) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(
        await rpcClient.rpc('record_contract_signature_v1', {
          p_command: Object.fromEntries(
            Object.entries(command).filter(([, value]) => value !== undefined && value !== ''),
          ),
        }),
      )
      if (response.error) throw new Error(response.error.message || 'CONTRACT_SIGNATURE_FAILED')
      const parsed = signatureResultSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('CONTRACT_SIGNATURE_RESPONSE_INVALID')
      return parsed.data
    },

    async recordManualSignature(command) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(
        await rpcClient.rpc('record_contract_manual_signature_v2', {
          p_command: Object.fromEntries(
            Object.entries(command).filter(([, value]) => value !== undefined && value !== ''),
          ),
        }),
      )
      if (response.error) throw new Error(response.error.message || 'CONTRACT_MANUAL_SIGNATURE_FAILED')
      const parsed = manualSignatureResultSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('CONTRACT_MANUAL_SIGNATURE_RESPONSE_INVALID')
      return parsed.data
    },

    async activateNoInvoice(command) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(
        await rpcClient.rpc('activate_contract_no_invoice_v1', { p_command: command }),
      )
      if (response.error) throw new Error(response.error.message || 'CONTRACT_NO_INVOICE_FAILED')
      const parsed = noInvoiceResultSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('CONTRACT_NO_INVOICE_RESPONSE_INVALID')
      return parsed.data
    },

    async changeStatus(command) {
      const rpcClient = client as unknown as SupabaseClient
      // Sprint C, partie C : v3 ajoute la conversion du Lead lié quand la
      // transition envoie le Contrat au Client (`a_signer`). v2 reste intacte,
      // non révoquée (convention v1/v2/v3 du dépôt).
      const response = asResponse(
        await rpcClient.rpc('change_contract_status_v3', {
          p_command: {
            contractId: command.contractId,
            status: command.status,
            ...(command.reason ? { reason: command.reason } : {}),
          },
        }),
      )
      if (response.error) throw new Error(response.error.message || 'CONTRACT_STATUS_FAILED')
    },

    async regularize(contractId) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(
        await rpcClient.rpc('regularize_contract_v1', { p_command: { contractId } }),
      )
      if (response.error) throw new Error(response.error.message || 'CONTRACT_REGULARIZE_FAILED')
    },
    async markNotInterested(contractIds) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(
        await rpcClient.rpc('mark_contracts_not_interested_v1', { p_command: { contractIds } }),
      )
      if (response.error) throw new Error(response.error.message || 'CONTRACT_NOT_INTERESTED_FAILED')
      const parsed = markNotInterestedResultSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('CONTRACT_NOT_INTERESTED_RESPONSE_INVALID')
      return {
        updated: parsed.data.updated.map((row) => ({
          contractId: row.contractId,
          invoicesCancelled: row.invoicesCancelled,
        })),
        skipped: parsed.data.skipped,
      }
    },
    async revertToSignature(contractId) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(
        await rpcClient.rpc('revert_contract_to_signature_v1', { p_command: { contractId } }),
      )
      if (response.error) throw new Error(response.error.message || 'CONTRACT_REVERT_SIGNATURE_FAILED')
    },
    async listAmendments(contractId) {
      const response = await client
        .from('contract_amendments')
        .select(
          'id,contract_id,amendment_number,kind,reason,changes,status,document_storage_path,applied_at,created_at',
        )
        .eq('contract_id', contractId)
        .order('amendment_number', { ascending: false })
      if (response.error) throw new Error(response.error.message || 'CONTRACT_AMENDMENTS_FAILED')
      const parsed = z.array(contractAmendmentRowSchema).safeParse(response.data)
      if (!parsed.success) throw new Error('CONTRACT_AMENDMENTS_RESPONSE_INVALID')
      return parsed.data.map((row) => ({
        id: row.id,
        contractId: row.contract_id,
        amendmentNumber: row.amendment_number,
        kind: row.kind,
        reason: row.reason,
        changes: row.changes,
        status: row.status,
        ...(row.document_storage_path ? { documentStoragePath: row.document_storage_path } : {}),
        ...(row.applied_at ? { appliedAt: row.applied_at } : {}),
        createdAt: row.created_at,
      }))
    },
    async createAmendment(command) {
      const response = await client.rpc('create_contract_amendment_v1', {
        p_command: {
          contractId: command.contractId,
          kind: command.kind,
          reason: command.reason,
          changes: command.description.trim() ? { description: command.description.trim() } : {},
        },
      })
      if (response.error) throw new Error(response.error.message || 'CONTRACT_AMENDMENT_FAILED')
      const parsed = createContractAmendmentResultSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('CONTRACT_AMENDMENT_RESPONSE_INVALID')
      return parsed.data
    },
    async applyAmendment(amendmentId) {
      const response = await client.rpc('apply_contract_amendment_v1', { p_command: { amendmentId } })
      if (response.error) throw new Error(response.error.message || 'CONTRACT_AMENDMENT_APPLY_FAILED')
      const parsed = applyContractAmendmentResultSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('CONTRACT_AMENDMENT_RESPONSE_INVALID')
      return parsed.data
    },
    async archiveDocument(command) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(await rpcClient.rpc('archive_contract_document_v1', { p_command: command }))
      if (response.error) throw new Error(response.error.message || 'CONTRACT_DOCUMENT_FAILED')
      const parsed = documentResultSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('CONTRACT_DOCUMENT_RESPONSE_INVALID')
      return parsed.data
    },
    async sendDocument(command) {
      // L'envoi passe par l'Edge Function : le PDF est produit depuis
      // l'instantané serveur, déposé et archivé côté serveur, jamais confié au
      // navigateur. Aucun repli fixtures : un document contractuel fictif
      // adressé à un vrai Client serait une faute.
      const rpcClient = client as unknown as SupabaseClient
      const session = await rpcClient.auth.getSession()
      const token = session.data.session?.access_token
      if (!token) throw new Error('UNAUTHENTICATED')
      const response = await fetch(`${supabaseUrl}/functions/v1/send-contract`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          apikey: anonKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(command),
      })
      const body: unknown = await response.json().catch(() => null)
      if (!response.ok) throw new Error('CONTRACT_SEND_FAILED')
      const parsed = sendDocumentResultSchema.safeParse(body)
      if (!parsed.success) throw new Error('CONTRACT_SEND_RESPONSE_INVALID')
      const { documentVersion, ...rest } = parsed.data
      // Une reprise idempotente n'archive rien : la version reste absente
      // plutôt que rendue à zéro.
      return documentVersion === undefined ? rest : { ...rest, documentVersion }
    },
    async generateInvoices(command) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(
        await rpcClient.rpc('generate_contract_invoices_v3', { p_command: command }),
      )
      if (response.error) throw new Error(response.error.message || 'CONTRACT_INVOICES_FAILED')
      const parsed = invoicesResultSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('CONTRACT_INVOICES_RESPONSE_INVALID')
      return parsed.data
    },

    async listPendingClient() {
      const response = asResponse(
        await client
          .from('pending_contracts')
          .select(
            'contract_id,contract_number,client_id,client_name,service_address,client_phone,client_email,status_v2,total_amount,sent_at,last_follow_up_at,next_follow_up_at,follow_up_owner_id,follow_up_owner_email',
          )
          .order('sent_at', { ascending: true, nullsFirst: true }),
      )
      if (response.error) throw new Error(`PENDING_CONTRACTS_FAILED:${response.error.code ?? 'UNKNOWN'}`)
      const parsed = z.array(pendingContractRowSchema).safeParse(response.data)
      if (!parsed.success) throw new Error('PENDING_CONTRACTS_RESPONSE_INVALID')
      const items = parsed.data.map((row) => ({
        contractId: row.contract_id,
        contractNumber: row.contract_number,
        clientId: row.client_id,
        clientName: row.client_name ?? 'Client sans nom',
        ...(row.service_address ? { serviceAddress: row.service_address } : {}),
        ...(row.client_phone ? { clientPhone: row.client_phone } : {}),
        ...(row.client_email ? { clientEmail: row.client_email } : {}),
        statusV2: row.status_v2,
        totalAmountCents: Math.round((row.total_amount ?? 0) * 100),
        ...(row.sent_at ? { sentAt: row.sent_at } : {}),
        ...(row.last_follow_up_at ? { lastFollowUpAt: row.last_follow_up_at } : {}),
        ...(row.next_follow_up_at ? { nextFollowUpAt: row.next_follow_up_at } : {}),
        ...(row.follow_up_owner_id ? { followUpOwnerId: row.follow_up_owner_id } : {}),
        ...(row.follow_up_owner_email ? { followUpOwnerEmail: row.follow_up_owner_email } : {}),
      }))
      return {
        items,
        meta: { source: 'supabase', retrievedAt: new Date().toISOString(), anomalies: 0 },
      }
    },

    async scheduleFollowUp(command) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(
        await rpcClient.rpc('schedule_contract_follow_up_v1', {
          p_command: {
            contractId: command.contractId,
            nextFollowUpAt: command.nextFollowUpAt,
            ...(command.followUpOwnerId ? { followUpOwnerId: command.followUpOwnerId } : {}),
          },
        }),
      )
      if (response.error) {
        throw new Error(
          response.error.message || `CONTRACT_FOLLOW_UP_FAILED:${response.error.code ?? 'UNKNOWN'}`,
        )
      }
      const parsed = scheduleFollowUpResultSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('CONTRACT_FOLLOW_UP_RESPONSE_INVALID')
      return {
        contractId: parsed.data.contractId,
        nextFollowUpAt: parsed.data.nextFollowUpAt,
        ...(parsed.data.followUpOwnerId ? { followUpOwnerId: parsed.data.followUpOwnerId } : {}),
      }
    },

    async softDelete(contractId) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(
        await rpcClient.rpc('soft_delete_contract_v1', { p_command: { contractId } }),
      )
      if (response.error) throw new Error(response.error.message || 'CONTRACT_DELETE_FAILED')
    },

    async restore(contractId) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(await rpcClient.rpc('restore_contract_v1', { p_command: { contractId } }))
      if (response.error) throw new Error(response.error.message || 'CONTRACT_RESTORE_FAILED')
    },

    async listDeleted(filters: ContractDeletedListFilters): Promise<ContractDeletedPage> {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(
        // v2 : même liste que la v1, plus le filtre `flow` (migration 20261128090000).
        await rpcClient.rpc('list_deleted_contracts_v2', {
          p_command: {
            page: filters.page,
            pageSize: filters.pageSize,
            ...(filters.search ? { search: filters.search } : {}),
            ...(filters.flow ? { flow: filters.flow } : {}),
          },
        }),
      )
      if (response.error) throw new Error(response.error.message || 'CONTRACT_DELETED_LIST_FAILED')
      const parsed = z.object({ items: z.array(z.unknown()), total: z.number() }).safeParse(response.data)
      if (!parsed.success) throw new Error('CONTRACT_DELETED_LIST_RESPONSE_INVALID')
      const items: ContractRecord[] = []
      for (const raw of parsed.data.items) {
        const row = legacyContractRowSchema.safeParse(raw)
        if (row.success) items.push(mapLegacyContract(row.data))
      }
      return {
        items,
        page: filters.page,
        pageSize: filters.pageSize,
        total: parsed.data.total,
        meta: { source: 'supabase', retrievedAt: new Date().toISOString(), anomalies: 0 },
      }
    },

    async geocodeAddress(command) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(await rpcClient.rpc('geocode_contract_address_v1', { p_command: command }))
      if (response.error) throw new Error(response.error.message || 'CONTRACT_GEOCODE_FAILED')
    },
    async getBillingState(contractId) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(
        await rpcClient.rpc('contract_billing_state_v1', { p_contract_id: contractId }),
      )
      if (response.error) throw new Error(response.error.message || 'CONTRACT_BILLING_STATE_FAILED')
      const parsed = billingStateSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('CONTRACT_BILLING_STATE_RESPONSE_INVALID')
      const row = parsed.data
      return {
        contractId: row.contractId,
        priceCents: row.priceCents,
        priceTaxes: row.priceTaxes,
        totalCents: row.totalCents,
        paidCents: row.paidCents,
        remainingCents: row.remainingCents,
        ...(row.blocker ? { blocker: row.blocker } : {}),
        entries: row.entries.map((entry) => ({
          id: entry.id,
          order: entry.order,
          description: entry.description,
          amountCents: entry.amountCents,
          ...(entry.dueOn ? { dueOn: entry.dueOn } : {}),
          ...(entry.invoiceId ? { invoiceId: entry.invoiceId } : {}),
          ...(entry.invoiceNumber ? { invoiceNumber: entry.invoiceNumber } : {}),
          paid: entry.paid,
        })),
      }
    },
    async reviseBilling(command) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(
        await rpcClient.rpc('revise_contract_billing_v1', {
          p_command: {
            idempotencyKey: command.idempotencyKey,
            contractId: command.contractId,
            ...(command.priceCents === undefined
              ? {}
              : { priceCents: command.priceCents, priceOverride: command.priceOverride ?? false }),
            reason: command.reason,
            // Les montants ne partent jamais : le serveur les résout.
            entries: command.entries.map((entry) => ({
              description: entry.description,
              kind: entry.kind,
              value: entry.value,
              ...(entry.dueOn ? { dueOn: entry.dueOn } : {}),
            })),
          },
        }),
      )
      if (response.error) throw new Error(response.error.message || 'CONTRACT_BILLING_REVISION_FAILED')
      const parsed = billingRevisionResultSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('CONTRACT_BILLING_REVISION_RESPONSE_INVALID')
      return parsed.data
    },
    async updateAddress(command) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(
        await rpcClient.rpc('update_contract_address_v1', {
          p_command: {
            contractId: command.contractId,
            target: command.target,
            ...(command.locationId ? { locationId: command.locationId } : {}),
            ...(command.address
              ? {
                  addressLine1: command.address.line1,
                  addressLine2: command.address.line2 ?? '',
                  city: command.address.city ?? '',
                  province: command.address.province ?? '',
                  postalCode: command.address.postalCode ?? '',
                }
              : {}),
            ...(command.position
              ? { latitude: command.position.latitude, longitude: command.position.longitude }
              : {}),
          },
        }),
      )
      if (response.error) throw new Error(response.error.message || 'CONTRACT_ADDRESS_FAILED')
    },
    async setMarkersRequired(command) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(
        await rpcClient.rpc('set_contract_markers_required_v1', {
          p_command: {
            contractId: command.contractId,
            ...(command.locationId ? { locationId: command.locationId } : {}),
            required: command.required,
          },
        }),
      )
      if (response.error) throw new Error(response.error.message || 'CONTRACT_MARKERS_FAILED')
    },
    async setServiceLocationStatus(locationId, status) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(
        await rpcClient.rpc('set_service_location_status_v1', {
          p_location_id: locationId,
          p_status: status,
        }),
      )
      if (response.error) throw new Error(response.error.message || 'SERVICE_LOCATION_STATUS_FAILED')
    },
    async setOverviewHidden(contractId, hidden) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(
        await rpcClient.rpc('set_contract_overview_hidden_v1', {
          p_contract_id: contractId,
          p_hidden: hidden,
        }),
      )
      if (response.error) throw new Error(response.error.message || 'CONTRACT_OVERVIEW_HIDDEN_FAILED')
    },
  }
}
